import { CreativeMode } from '@domain/creative/environment';
import { Container } from '@studio/utils/di';
import { concatUrl } from '@studio/utils/url';
import { Animator } from './animator';
import { CreativeWrapper } from './creative-wrapper';
import { RichText } from './elements/rich-text/rich-text';
import { RichTextEditorService } from './elements/rich-text/rich-text.editor';
import { RichTextEditorKeyboardBindings } from './elements/rich-text/rich-text.editor.keyboard-bindings';
import { RichTextEditorMouseBindings } from './elements/rich-text/rich-text.editor.mouse-bindings';
import { RichTextEditorStyleResolver } from './elements/rich-text/rich-text.editor.style-resolver';
import { RichTextHtmlResolver } from './elements/rich-text/rich-text.html-resolver';
import { RichTextSelectionService } from './elements/rich-text/rich-text.selection';
import { Renderer } from './renderer';
const _useless = ''; // Prevents ending strip block comment above from disappearing
export async function registerFeaturesByFiles(files, origin, container) {
    const registerTasks = [];
    __webpack_public_path__ = origin && concatUrl(origin, '/scripts/');
    for (const file of files) {
        if (!file.id.startsWith('preload')) {
            continue;
        }
        const matches = /^([\w-]+)/.exec(file.url);
        switch (matches[1]) {
            case 'widget':
                registerTasks.push(registerWidgetFeature(container));
                break;
            case 'feed':
                registerTasks.push(registerFeedStoreFeature(container));
                break;
            case 'video':
                registerTasks.push(registerVideoFeature(container));
                break;
        }
    }
    await Promise.all(registerTasks);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    __webpack_public_path__ = '';
}
async function registerFeedStoreFeature(container) {
    const { FeedStore: FeedStoreC } = await import(
    /* webpackChunkName: "feed" */ './elements/feed/feed-store');
    container.registerFactory_m(13 /* T.FEED_STORE_FACTORY */, factoryContainer => (brandId) => {
        const renderer = factoryContainer.resolve(3 /* T.RENDERER */);
        const env = factoryContainer.resolve(2 /* T.ENVIRONMENT */);
        return new FeedStoreC(renderer, env, brandId);
    });
    container.resolve(13 /* T.FEED_STORE_FACTORY */);
}
async function registerWidgetFeature(container) {
    const { WidgetRenderer: WidgetRendererC, AdApi: AdApiC, CreativeApi: CreativeApiC, TimelineApi: TimelineApiC } = await import(/* webpackChunkName: "widget" */ './elements/widget/widget.module');
    container.register_m(4 /* T.WIDGET_RENDERER */, WidgetRendererC);
    container.register_m(8 /* T.AD_API */, AdApiC);
    container.register_m(9 /* T.CREATIVE_API */, CreativeApiC);
    container.register_m(10 /* T.TIMELINE_API */, TimelineApiC);
}
async function registerVideoFeature(container) {
    const { VideoRenderer: VideoRendererC } = await import(
    /* webpackChunkName: "video" */ './elements/video/video.module');
    container.registerFactory_m(14 /* T.VIDEO_RENDERER_FACTORY */, factoryContainer => (viewElement) => {
        const animator = factoryContainer.resolve(5 /* T.ANIMATOR */);
        const renderer = factoryContainer.resolve(3 /* T.RENDERER */);
        const env = factoryContainer.resolve(2 /* T.ENVIRONMENT */);
        const ad = factoryContainer.resolve(0 /* T.AD */);
        return new VideoRendererC(ad, renderer, animator, env, viewElement);
    });
    container.register_m(7 /* T.VIDEO_RENDERER */, VideoRendererC);
}
export function createCreativeContainer(environment) {
    const IN_STUDIO = environmentIsStudio(environment);
    const creativeContainer = new Container();
    creativeContainer.register_m(15 /* T.RICH_TEXT */, RichText);
    creativeContainer.register_m(5 /* T.ANIMATOR */, Animator);
    creativeContainer.registerFactory_m(22 /* T.RICH_TEXT_FACTORY */, container => (text, rootElement, options) => {
        return new RichText(text, rootElement, options, environment);
    });
    creativeContainer.registerFactory_m(11 /* T.RENDERER_FACTORY */, container => (option) => {
        const document = container.resolve(1 /* T.CREATIVE_DATA */);
        const env = container.resolve(2 /* T.ENVIRONMENT */);
        const renderer = new Renderer(container, document, option, env);
        const createFeedStore = container.maybeResolve(13 /* T.FEED_STORE_FACTORY */);
        container.register_m(3 /* T.RENDERER */, renderer);
        if (createFeedStore) {
            renderer.feedStore = createFeedStore(option.brandId);
        }
        const widgetRenderer = container.maybeResolve(4 /* T.WIDGET_RENDERER */);
        renderer.WidgetRenderer = widgetRenderer;
        const ad = container.maybeResolve(0 /* T.AD */);
        renderer.ad = ad;
        return renderer;
    });
    creativeContainer.registerFactory_m(12 /* T.CREATIVE_FACTORY */, container => (creativeData, option, ad) => {
        container.register_m(1 /* T.CREATIVE_DATA */, creativeData);
        container.register_m(0 /* T.AD */, ad);
        return new CreativeWrapper(option, container);
    });
    /** Remove modules that are autoresolved inside of studio for the output */
    return creativeContainer;
}
function environmentIsStudio(environment) {
    return (environment.MODE === CreativeMode.ManageView ||
        environment.MODE === CreativeMode.DesignView ||
        environment.MODE === CreativeMode.TranslationPanel);
}
